import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api/endpoints';
import type { Expense, ExpenseList, ExpensePaymentMode } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { RankedBars } from '../components/charts';
import { useToast } from '../components/Toast';
import { Badge, Button, Card, EmptyState, ErrorNote, Input, Modal, Spinner } from '../components/ui';
import { useDataVersion } from '../hooks/useDataVersion';
import { formatMoney } from '../lib/format';

const PAGE_SIZE = 50;

// Offered before anything has been recorded; once the cafe has its own
// categories, those are suggested instead (most used spelling first).
const STARTER_CATEGORIES = [
  'Rent',
  'Electricity',
  'Internet',
  'Salaries',
  'Repairs & maintenance',
  'Cleaning',
  'Supplies',
  'Other',
];

const MODE_LABEL: Record<ExpensePaymentMode, string> = {
  cash: 'Cash',
  upi: 'UPI',
  bank: 'Bank',
  card: 'Card',
};

function isoDay(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
const today = () => isoDay(new Date());
const monthStart = () => {
  const date = new Date();
  return isoDay(new Date(date.getFullYear(), date.getMonth(), 1));
};
function longDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function ExpenseDialog({
  expense,
  categories,
  onClose,
  onSaved,
}: {
  /** Null to record a new one. */
  expense: Expense | null;
  categories: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [spentOn, setSpentOn] = useState(expense?.spent_on ?? today());
  const [category, setCategory] = useState(expense?.category ?? '');
  const [amount, setAmount] = useState(expense ? String(expense.amount) : '');
  const [mode, setMode] = useState<ExpensePaymentMode>(expense?.payment_mode ?? 'cash');
  const [note, setNote] = useState(expense?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountValue = Number(amount);
  const valid = category.trim() !== '' && Number.isFinite(amountValue) && amountValue > 0 && spentOn !== '';
  const suggestions = categories.length ? categories : STARTER_CATEGORIES;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    const input = {
      spent_on: spentOn,
      category: category.trim(),
      amount: amountValue,
      payment_mode: mode,
      note: note.trim() || null,
    };
    try {
      if (expense) {
        await api.expenses.update(expense.expense_id, input);
        toast.success('Expense updated.');
      } else {
        await api.expenses.create(input);
        toast.success(`${formatMoney(amountValue)} for ${input.category} recorded.`);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={expense ? 'Edit expense' : 'Record an expense'} onClose={onClose}>
      <form className="space-y-4" onSubmit={submit}>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="Amount (₹)"
            value={amount}
            onChange={(event) => setAmount(event.target.value.replace(/[^0-9.]/g, ''))}
            inputMode="decimal"
            placeholder="4200"
            autoFocus
          />
          <Input
            label="Date spent"
            type="date"
            value={spentOn}
            max={today()}
            onChange={(event) => setSpentOn(event.target.value)}
          />
        </div>

        <div>
          <Input
            label="What for"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            placeholder="Electricity"
            list="expense-categories"
          />
          <datalist id="expense-categories">
            {suggestions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
          {/* One tap for the common ones, so the list does not grow
              "electricty" and "Electricity bill" next to "Electricity". */}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {suggestions.slice(0, 8).map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setCategory(name)}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                  category.trim().toLowerCase() === name.toLowerCase()
                    ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                    : 'border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-slate-300">Paid by</span>
          <div className="grid grid-cols-4 gap-2">
            {(Object.keys(MODE_LABEL) as ExpensePaymentMode[]).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setMode(option)}
                className={`min-h-11 rounded-lg border text-sm font-medium transition-colors ${
                  mode === option
                    ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                    : 'border-slate-800 text-slate-300 hover:bg-slate-800'
                }`}
              >
                {MODE_LABEL[option]}
              </button>
            ))}
          </div>
        </div>

        <Input
          label="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="September bill, meter 2"
        />

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="submit" className="flex-1" disabled={busy || !valid}>
            {busy ? 'Saving…' : expense ? 'Save changes' : valid ? `Record ${formatMoney(amountValue)}` : 'Record expense'}
          </Button>
          <Button type="button" variant="secondary" className="flex-1" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Money going out. Anyone at the counter can record what they paid for;
// correcting or deleting an entry afterwards is an admin's job (the backend
// enforces it too). Reports read these for the profit figure.
export function ExpensesPage() {
  const toast = useToast();
  const { isAdmin } = useAuth();
  const version = useDataVersion();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [category, setCategory] = useState('');
  const [shown, setShown] = useState(PAGE_SIZE);
  const [data, setData] = useState<ExpenseList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Expense | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Expense | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api.expenses
      .list({ from, to, category: category || undefined, limit: shown }, controller.signal)
      .then((result) => {
        setData(result);
        setError(null);
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [from, to, category, shown, version]);

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.expenses.remove(deleting.expense_id);
      toast.success(`${formatMoney(deleting.amount)} for ${deleting.category} deleted.`);
      setDeleting(null);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">Expenses</h1>
        <Button onClick={() => setEditing('new')}>Record expense</Button>
      </div>

      <Card className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Input label="From" type="date" value={from} max={to} onChange={(event) => { setFrom(event.target.value); setShown(PAGE_SIZE); }} />
        <Input label="To" type="date" value={to} min={from} onChange={(event) => { setTo(event.target.value); setShown(PAGE_SIZE); }} />
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-slate-300">Category</span>
          <select
            value={category}
            onChange={(event) => { setCategory(event.target.value); setShown(PAGE_SIZE); }}
            className="min-h-11 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 text-base text-slate-100 focus:border-sky-500 focus:outline-none"
          >
            <option value="">All categories</option>
            {(data?.categories ?? []).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-end">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => {
              setFrom(monthStart());
              setTo(today());
              setCategory('');
              setShown(PAGE_SIZE);
            }}
          >
            This month
          </Button>
        </div>
      </Card>

      {error && <ErrorNote>{error}</ErrorNote>}

      {loading && !data ? (
        <Spinner label="Loading expenses…" />
      ) : data && (
        <>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(14rem,18rem)_1fr]">
            <Card>
              <p className="text-xs uppercase tracking-wide text-slate-500">Spent</p>
              <p className="mt-1 text-3xl font-semibold tabular-nums text-slate-100">
                {formatMoney(data.total_amount)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {data.total_count} expense{data.total_count === 1 ? '' : 's'} ·{' '}
                {formatMoney(data.cash_amount)} from the cash drawer
              </p>
            </Card>
            <Card>
              {data.by_category.length === 0 ? (
                <p className="text-sm text-slate-500">Nothing recorded in these dates.</p>
              ) : (
                <RankedBars
                  formatValue={formatMoney}
                  rows={data.by_category.map((row) => ({
                    name: row.category,
                    value: row.amount,
                    note: `×${row.count}`,
                  }))}
                />
              )}
            </Card>
          </div>

          {data.expenses.length === 0 ? (
            <EmptyState title="No expenses in these dates.">
              Record rent, bills, salaries and anything bought for the cafe, so the
              reports can show profit and not just takings.
            </EmptyState>
          ) : (
            <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
              {data.expenses.map((expense) => (
                <li
                  key={expense.expense_id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-slate-100">
                      {expense.category}
                      <span className="ml-2 align-middle">
                        <Badge tone={expense.payment_mode === 'cash' ? 'amber' : 'slate'}>
                          {MODE_LABEL[expense.payment_mode]}
                        </Badge>
                      </span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {longDay(expense.spent_on)}
                      {expense.recorded_by_name && ` · ${expense.recorded_by_name}`}
                      {expense.note && ` · ${expense.note}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold tabular-nums text-slate-100">
                      {formatMoney(expense.amount)}
                    </span>
                    {isAdmin && (
                      <>
                        <Button variant="ghost" className="min-h-8 px-2 py-1 text-xs" onClick={() => setEditing(expense)}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          className="min-h-8 px-2 py-1 text-xs text-rose-300 hover:text-rose-200"
                          onClick={() => setDeleting(expense)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {data.expenses.length < data.total_count && (
            <Button
              variant="secondary"
              className="w-full"
              disabled={loading}
              onClick={() => setShown((current) => current + PAGE_SIZE)}
            >
              {loading ? 'Loading…' : `Show more (${data.total_count - data.expenses.length} older)`}
            </Button>
          )}
        </>
      )}

      {editing && (
        <ExpenseDialog
          expense={editing === 'new' ? null : editing}
          categories={data?.categories ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => undefined}
        />
      )}
      {deleting && (
        <Modal title="Delete this expense?" onClose={() => setDeleting(null)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              {formatMoney(deleting.amount)} for {deleting.category} on {longDay(deleting.spent_on)}.
              Only for an entry made twice or by mistake — it comes off the reports.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="danger" className="flex-1" disabled={busy} onClick={remove}>
                {busy ? 'Deleting…' : 'Delete'}
              </Button>
              <Button variant="secondary" className="flex-1" disabled={busy} onClick={() => setDeleting(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
